import { Icon24ArrowLeftOutline } from '@vkontakte/icons';
import { IconButton } from '@vkontakte/vkui';
import React, { useMemo } from 'react';
import { tariffBuyCustomTitle } from 'reactApp/appHelpers/featuresHelpers';
import { tariffCustomTitleMap } from 'reactApp/appHelpers/featuresHelpers/features/tariffCustomTitleMap';
import { vigboTariffs } from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { FastCheckoutLightSidebar } from 'reactApp/components/VkBuyModalWithSidebar/FastCheckoutLightSidebar/FastCheckoutLightSidebar';
import { FastCheckoutSidebar } from 'reactApp/components/VkBuyModalWithSidebar/FastCheckoutSidebar/FastCheckoutSidebar';
import { TrialPromoSidebar } from 'reactApp/components/VkBuyModalWithSidebar/TrialPromoSidebar/TrialPromoSidebar';
import styles from 'reactApp/components/VkBuyModalWithSidebar/VkBuyModalWithSidebar.css';
import { type Options, EPaymentModalType } from 'reactApp/components/VkBuyModalWithSidebar/VkBuyModalWithSidebar.types';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import type { Product, Tariff } from 'reactApp/types/Billing';
import { PaymentFooter } from 'reactApp/ui/PaymentFooter/PaymentFooter';
import { getPeriodName } from 'reactApp/utils/Period';

interface HookProps {
    type: EPaymentModalType;
    product: Product;
    changeProduct: (id: string) => void;
    onClickTariffs: () => void;
    showBackButton?: boolean;
    onBackClick?: () => void;
    tariff: Tariff;
    options?: Options;
    featuresList?: string[];
    isFrame?: boolean;
    title?: string;
    hideOtherTariffs?: boolean;
    hideSwitchSidebarBlock?: boolean;
    isMidas?: boolean;
}

export const useContent = ({
    type,
    product,
    changeProduct,
    onClickTariffs,
    showBackButton,
    onBackClick = () => {},
    tariff,
    options,
    featuresList,
    isFrame,
    title,
    hideOtherTariffs = false,
    hideSwitchSidebarBlock = false,
    isMidas,
}: HookProps) => {
    const sidebar = useMemo(() => {
        if (type === EPaymentModalType.trial || type === EPaymentModalType.attachesTrial) {
            return <TrialPromoSidebar product={product} options={options} />;
        }

        if (type === EPaymentModalType.fastcheckout && isMidas) {
            return (
                <FastCheckoutLightSidebar
                    product={product}
                    onClickTariffs={onClickTariffs}
                    featuresList={featuresList}
                    hideOtherTariffs={hideOtherTariffs}
                />
            );
        }

        if (type === EPaymentModalType.fastcheckout) {
            return (
                <FastCheckoutSidebar
                    product={product}
                    onProductChange={changeProduct}
                    onClickTariffs={onClickTariffs}
                    tariff={tariff}
                    featuresList={featuresList}
                    isBuy={isFrame}
                    hideOtherTariffs={hideOtherTariffs}
                    hideSwitchSidebarBlock={hideSwitchSidebarBlock}
                />
            );
        }
    }, [type, product, changeProduct, onClickTariffs, tariff, isFrame, hideOtherTariffs, isMidas]);

    const headerText = useMemo(() => {
        if (title) {
            return title;
        }

        const { title: titleFromQuery = null, action = null } = getQueryParams();

        if (product?.id && tariffCustomTitleMap.hasOwnProperty(product.id)) {
            return tariffCustomTitleMap[product.id];
        }

        if (action === 'request-payment' && tariffBuyCustomTitle?.hasOwnProperty(titleFromQuery)) {
            return tariffBuyCustomTitle[titleFromQuery];
        }

        const trialPeriodName = product.hasTrial && product.trialPeriod && getPeriodName(product.trialPeriod, false, true);
        const discountPeriodName = product.hasDiscount && product.discountPeriod && getPeriodName(product.discountPeriod, false, true);
        const spaceText = product.space.original > SPACE_LIST.kb_1 ? product.space.value : '';

        if (product?.isFamily && product?.isOnlyFeatures) {
            return trialPeriodName ? `Подключить семейные функции на ${trialPeriodName} бесплатно` : 'Подключить семейные функции';
        }

        if (product?.isFamily) {
            return trialPeriodName ? `Семейная подписка Mail Space на ${trialPeriodName} бесплатно` : 'Оплата семейной подписки Mail Space';
        }

        if (product?.id && vigboTariffs.includes(product.id)) {
            return trialPeriodName
                ? `Подписка для фотографов Mail Space и Vigbo на ${trialPeriodName} бесплатно`
                : `Подписка для фотографов Mail Space и Vigbo`;
        }

        if (isMidas && type === EPaymentModalType.fastcheckout) {
            if (trialPeriodName) {
                return `Подписка Mail Space на ${trialPeriodName} бесплатно`;
            }

            return discountPeriodName
                ? `Подписка Mail Space ${spaceText} на ${discountPeriodName} со скидкой`
                : 'Оплата подписки Mail Space';
        }

        if (type === EPaymentModalType.fastcheckout) {
            return trialPeriodName ? `Подписка Mail Space на ${trialPeriodName} бесплатно` : 'Оплата подписки Mail Space';
        }
    }, [type, title, product]);

    const header = useMemo(() => {
        if (headerText) {
            return (
                <div className={styles.headerContainer}>
                    {showBackButton && (
                        <IconButton data-qa-id="back" hasHover={false} onClick={onBackClick} aria-label="Назад">
                            <Icon24ArrowLeftOutline />
                        </IconButton>
                    )}
                    <div className={styles.header}>{headerText}</div>
                </div>
            );
        }
    }, [headerText]);

    const footer = useMemo(() => {
        if (type === EPaymentModalType.fastcheckout) {
            return <PaymentFooter productId={product?.id} />;
        }
    }, [product?.id, type]);

    return {
        sidebar,
        header,
        footer,
    };
};
